import { FC, useEffect, useState } from "react";
import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import {
  PageHeading,
  SectionHeading,
  TeamHeading,
} from "@/components/Layout/Parts";
import DateRangeFilter from "@/components/DateRangeFilter";
import PredictionCard from "./PredictionCard";
import { api } from "@/utils/api";
import { ReleasePredictionResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import ContentSpinner from "@/components/ContentSpinner";
import { ReleasePredictionStatus } from "@/utils/enums";
import Text from "@/components/Text";
import FacebookPagesFilter from "@/components/ChannelsFilter/FacebookPagesFilter";

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 0 0 12px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledChannelsFilter = styled(FacebookPagesFilter)`
  margin-bottom: 21px;
`;

const EmptyCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  height: 144px;

  svg {
    color: ${({ theme }) => theme.colors.gray};
    margin-right: 8px;
  }
`;

const DistributionPredictions: FC = () => {
  const [dateRange, setDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>({});

  const alerts = useAlerts();

  const [selectedChannels, setSelectedChannels] = useState<number[]>([]);

  const {
    data: predictions,
    isLoading,
    isError,
  } = useQuery(
    [
      "release_predictions",
      {
        selectedChannels,
        dateFrom: dateRange.rangeStart?.toFormat("yyyy-LL-dd"),
        dateTo: dateRange.rangeEnd?.toFormat("yyyy-LL-dd"),
        status: ReleasePredictionStatus.PENDING,
      },
    ],
    () =>
      api.get<never, ReleasePredictionResource[]>(`/v1/release-predictions`, {
        params: {
          "filter[status]": ReleasePredictionStatus.PENDING,
          ...(!!selectedChannels.length && {
            "filter[channel_id]": selectedChannels.join(","),
          }),
          ...(!!dateRange.rangeStart && {
            "filter[date_from]": dateRange.rangeStart.toFormat("yyyy-LL-dd"),
          }),
          ...(!!dateRange.rangeEnd && {
            "filter[date_to]": dateRange.rangeEnd.toFormat("yyyy-LL-dd"),
          }),
        },
      }),
    {
      staleTime: 120 * 1000,
    }
  );

  useEffect(() => {
    if (isError) {
      alerts.error();
    }
  }, [isError, alerts]);

  return (
    <>
      <TeamHeading />
      <PageHeading>Predictions</PageHeading>
      <Header>
        <DateRangeFilter
          selectedDateRange={dateRange}
          setSelectedDateRange={setDateRange}
        />
      </Header>
      <StyledChannelsFilter setChannels={setSelectedChannels} />
      <SectionHeading>predictions list</SectionHeading>
      {isLoading ? (
        <ContentSpinner />
      ) : predictions?.length ? (
        <List>
          {predictions?.map((prediction) => (
            <PredictionCard key={prediction.id} prediction={prediction} />
          ))}
        </List>
      ) : (
        <EmptyCard>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.40649 6.6665H13.3324"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M7.40649 9.62988H13.3324"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M7.40649 12.5928H10.3695"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.025 18.5185H2.96144C2.76499 18.5185 2.57658 18.4369 2.43766 18.2917C2.29875 18.1464 2.2207 17.9495 2.2207 17.7441V2.25586C2.2207 2.05047 2.29875 1.8535 2.43766 1.70827C2.57658 1.56303 2.76499 1.48145 2.96144 1.48145H17.7763C17.9727 1.48145 18.1611 1.56303 18.3 1.70827C18.439 1.8535 18.517 2.05047 18.517 2.25586V12.7768C18.517 12.8785 18.4978 12.9792 18.4606 13.0732C18.4234 13.1671 18.3688 13.2525 18.3 13.3244L13.5488 18.2917C13.48 18.3636 13.3983 18.4206 13.3085 18.4595C13.2186 18.4985 13.1223 18.5185 13.025 18.5185V18.5185Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M18.4502 12.5918H13.332V17.7103"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <Text $color="gray" $type="caption">
            No predictions yet
          </Text>
        </EmptyCard>
      )}
    </>
  );
};

export default DistributionPredictions;
